import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Rooms = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const rooms = [
    {
      name: 'Chambre 1 — Rez-de-Chaussée',
      nameEn: 'Room 1 — Ground Floor',
      description: 'Élégante chambre double au rez-de-chaussée offrant un accès direct au patio central. Décoration raffinée dans des tons neutres, salle de bain privative avec douche à l\'italienne.',
      descriptionEn: 'Elegant double room on the ground floor with direct access to the central patio. Refined decoration in neutral tones, private bathroom with walk-in shower.',
      bed: isEn ? 'Double bed 160 cm' : 'Lit double 160 cm',
      images: [
        '/images/tmrrlg9y_3E5EA15E-CE3E-4345-8E47-1BD8F1A637DB.JPG',
        '/images/w96gi5t8_EA5C19F3-D38D-4566-AE58-3A7EA7F86695.JPG',
        '/images/kknwhtma_CEECD2C6-85F1-464E-BFE3-7883F1B37A83.JPG'
      ]
    },
    {
      name: 'Chambre 2 — Premier Étage',
      nameEn: 'Room 2 — First Floor',
      description: 'Chambre double au premier étage avec une superbe tête de lit sculptée aux motifs de palmiers. Tons terracotta chaleureux, salle de bain en zellige traditionnel.',
      descriptionEn: 'Double room on the first floor with a stunning carved headboard featuring palm tree motifs. Warm terracotta tones, traditional zellige bathroom.',
      bed: isEn ? 'Double bed 160 cm' : 'Lit double 160 cm',
      images: [
        '/images/8ffg3exq_20E4725C-713F-4E05-AA0D-39CF5F66FFAB.JPG',
        '/images/7jqqnvir_BF70FED1-EB16-4FEA-B7FB-07028A58BBE4.JPG',
        '/images/nyu22t37_71B70556-DF30-4AAA-9A8C-80015415F27F.JPG'
      ]
    },
    {
      name: 'Chambre 3 — Twin',
      nameEn: 'Room 3 — Twin',
      description: 'Chambre twin lumineuse avec deux lits simples et une magnifique tête de lit sculptée avec rétro-éclairage LED. Salle de bain aux carreaux bleu turquoise, vasque artisanale marocaine.',
      descriptionEn: 'Bright twin room with two single beds and a magnificent carved headboard with LED backlighting. Turquoise blue tiled bathroom, Moroccan artisan sink.',
      bed: isEn ? '2 single beds' : '2 lits simples',
      images: [
        '/images/jd0140qn_509BE973-3C73-44A3-92BD-3A92AC636371.JPG',
        '/images/99a1hz65_5D19E030-2A82-4B93-B1C6-75FA4EB3B663.JPG',
        '/images/r5ui24qq_024EE62C-F511-40FA-9022-C8C0F114885A.JPG'
      ]
    },
    {
      name: 'Chambre 4 — Premier Étage',
      nameEn: 'Room 4 — First Floor',
      description: 'Chambre double spectaculaire avec tête de lit sculptée aux arches majestueuses et motifs de palmiers. Tons dorés et ocre, ambiance chaleureuse et luxueuse.',
      descriptionEn: 'Spectacular double room with carved headboard featuring majestic arches and palm tree motifs. Golden and ochre tones, warm and luxurious atmosphere.',
      bed: isEn ? 'Double bed 160 cm' : 'Lit double 160 cm',
      images: [
        '/images/04ek7zev_AA301CCE-A7D4-46BD-8C8F-26F68A1D8E74.JPG',
        '/images/ryxt7wjx_A8297DCB-CF88-4C36-8CBF-7CCBD8A45E09.JPG',
        '/images/xjgywd5j_8962C7C9-BC64-49C2-8157-139BCFC2B47A.JPG'
      ]
    },
    {
      name: 'Chambre 5 — Premier Étage',
      nameEn: 'Room 5 — First Floor', 
      description: 'Chambre double unique avec fresque murale tropicale peinte à la main représentant palmiers, bananiers et fleurs exotiques. Salle de bain aux carreaux vert palmier. Une véritable œuvre d\'art.',
      descriptionEn: 'Unique double room with hand-painted tropical mural featuring palm trees, banana trees and exotic flowers. Palm green tiled bathroom. A true work of art.',
      bed: isEn ? 'Double bed 160 cm' : 'Lit double 160 cm',
      images: [
        '/images/7xz1gdq6_BF339CCE-97DD-4062-A59C-857196241836.JPG',
        '/images/qu2pzr6t_4E7999C0-526D-4BEF-B559-BB77C9F4783E.JPG',
        '/images/d9u7hvqk_ABFE0EF3-91B6-4868-8AF8-D4250D41F558.JPG'
      ]
    }
  ];

  const amenities = isEn ? [
    'Individual air conditioning',
    'Private bathroom',
    'Luxury toiletries',
    'Premium bed linen',
    'Bathrobes and slippers',
    'High-speed Wi-Fi'
  ] : [
    'Climatisation individuelle',
    'Salle de bain privative',
    'Produits de toilette de luxe',
    'Linge de lit premium',
    'Peignoirs et chaussons',
    'Wi-Fi haut débit'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <div data-testid="rooms-page" className="bg-[#FAF8F5] min-h-screen">
      <section className="pt-48 pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-6 text-center"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {isEn ? 'Accommodation' : 'Hébergement'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-serif text-5xl md:text-7xl text-[#1A1A1A] mb-8 text-center"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
          >
            {t('rooms.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-[#1A1A1A]/80 max-w-3xl mx-auto text-center leading-relaxed mb-16"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
          >
            {isEn 
              ? 'Five exceptional rooms, each with its own chromatic identity and unique character. All have a private bathroom, air conditioning and high-speed Wi-Fi.'
              : 'Cinq chambres d\'exception, chacune avec sa propre identité chromatique et son caractère unique. Toutes disposent d\'une salle de bain privative, de la climatisation et du Wi-Fi haut débit.'}
          </motion.p>

          {/* Amenities */}
          <motion.div {...fadeInUp} className="flex flex-wrap justify-center gap-6 mb-24">
            {amenities.map((amenity, index) => (
              <div
                key={index}
                className="bg-white/60 backdrop-blur-sm px-6 py-3 border border-[#D4AF37]/30 text-[#1A1A1A]/70 text-sm shadow-sm"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {amenity}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Rooms Grid */}
      <section className="pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-7xl mx-auto space-y-32">
          {rooms.map((room, index) => (
            <motion.div
              key={index}
              {...fadeInUp}
              transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            >
              {/* Text Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="w-16 h-[2px] bg-[#D4AF37]"></div>
                <h2
                  className="font-serif text-4xl md:text-5xl text-[#1A1A1A]"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
                >
                  {isEn ? room.nameEn : room.name}
                </h2>
                <p
                  className="text-base text-[#1A1A1A]/70 leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
                >
                  {isEn ? room.descriptionEn : room.description}
                </p>
                <div className="flex items-center gap-3 text-[#B8860B] text-sm uppercase tracking-wider">
                  <div className="w-8 h-[1px] bg-[#D4AF37]"></div>
                  <span style={{ fontFamily: 'Montserrat, sans-serif' }}>{room.bed}</span>
                </div>
              </div>

              {/* Images Grid */}
              <div className={`grid grid-cols-2 gap-4 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="col-span-2 relative overflow-hidden group h-96 border border-[#D4AF37]/30 shadow-lg">
                  <img
                    src={room.images[0]}
                    alt={isEn ? room.nameEn : room.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                  />
                </div>
                {room.images.slice(1).map((img, imgIndex) => (
                  <div
                    key={imgIndex}
                    className="relative overflow-hidden group h-64 border border-[#D4AF37]/30 shadow-lg"
                  >
                    <img
                      src={img}
                      alt={`${isEn ? room.nameEn : room.name} ${imgIndex + 2}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Rooms;
